import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, Loader2, CheckCircle, Clock, AlertCircle, XCircle,
  Home, User, PoundSterling, FileText, Key, Calendar, Mail, Shield,
  ClipboardCheck, CreditCard, FileCheck, UserCheck
} from "lucide-react";
import { API } from "@/App";

const STATUS_CONFIG = {
  setup: { label: "Setting Up", color: "bg-blue-100 text-blue-700", icon: Clock },
  references: { label: "References", color: "bg-amber-100 text-amber-700", icon: ClipboardCheck },
  active: { label: "Active", color: "bg-green-100 text-green-700", icon: CheckCircle },
  ended: { label: "Ended", color: "bg-slate-100 text-slate-700", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: XCircle }
};

const MILESTONE_CONFIG = {
  holding_deposit_received: { label: "Holding Deposit Received", icon: CreditCard },
  references_started: { label: "References Started", icon: ClipboardCheck },
  references_passed: { label: "References Passed", icon: CheckCircle },
  tenancy_agreement_sent: { label: "Tenancy Agreement Sent", icon: FileText },
  right_to_rent_complete: { label: "Right to Rent Complete", icon: Shield },
  move_in_monies_received: { label: "Move-in Monies Received", icon: PoundSterling },
  inventory_arranged: { label: "Inventory Arranged", icon: FileCheck },
  tenancy_start_confirmed: { label: "Tenancy Start Confirmed", icon: Key }
};

const MILESTONE_STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700" },
  completed: { label: "Completed", color: "bg-green-100 text-green-700" },
  blocked: { label: "Blocked", color: "bg-red-100 text-red-700" }
};

export const TenancyProgressionPage = () => {
  const { tenancyId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [tenancy, setTenancy] = useState(null);
  const [listing, setListing] = useState(null);
  const [offer, setOffer] = useState(null);
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("milestones");
  
  // Modals
  const [showDatesModal, setShowDatesModal] = useState(false);
  const [datesForm, setDatesForm] = useState({
    start_date: "",
    end_date: "",
    move_in_date: ""
  });
  
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const fetchTenancy = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/progression/tenancies/${tenancyId}`, { headers });
      setTenancy(res.data.tenancy);
      setListing(res.data.listing);
      setOffer(res.data.offer);
      setProgress(res.data.progress || 0);
      
      // Pre-fill dates form
      if (res.data.tenancy) {
        setDatesForm({
          start_date: res.data.tenancy.start_date || "",
          end_date: res.data.tenancy.end_date || "",
          move_in_date: res.data.tenancy.move_in_date || ""
        });
      }
    } catch (error) {
      console.error("Error fetching tenancy:", error);
      toast.error("Failed to load tenancy");
    } finally {
      setLoading(false);
    }
  }, [token, tenancyId]);

  useEffect(() => {
    fetchTenancy();
  }, [fetchTenancy]);

  const handleMilestoneUpdate = async (milestoneKey, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/progression/tenancies/milestone`, {
        tenancy_id: tenancyId,
        milestone_key: milestoneKey,
        status: newStatus,
        date: newStatus === "completed" ? new Date().toISOString().split("T")[0] : null
      }, { headers });
      
      toast.success("Milestone updated");
      fetchTenancy();
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const handleUpdateDates = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/progression/tenancies/dates`, {
        tenancy_id: tenancyId,
        ...datesForm
      }, { headers });
      
      toast.success("Dates updated");
      setShowDatesModal(false);
      fetchTenancy();
    } catch (error) {
      toast.error("Failed to update dates");
    }
  };

  const handleCancel = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/progression/tenancies/${tenancyId}/cancel?reason=${encodeURIComponent(cancelReason)}`, {}, { headers });
      
      toast.success("Tenancy cancelled");
      setShowCancelModal(false);
      fetchTenancy();
    } catch (error) {
      toast.error("Failed to cancel tenancy");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!tenancy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Tenancy not found</p>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[tenancy.status] || STATUS_CONFIG.setup;
  const StatusIcon = statusConfig.icon;
  const milestones = tenancy.milestones || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-white/80 hover:text-white hover:bg-white/10">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={`${statusConfig.color} px-3 py-1`}>
                  <StatusIcon className="w-4 h-4 mr-1" />
                  {statusConfig.label}
                </Badge>
                <Badge variant="outline" className="text-white border-white/40">
                  Tenancy Setup
                </Badge>
              </div>
              <h1 className="text-2xl font-bold">{tenancy.address}</h1>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-sm">Monthly Rent</p>
              <p className="text-3xl font-bold">
                £{(tenancy.rent_pcm || 0).toLocaleString()}
              </p>
              <div className="mt-2">
                <p className="text-white/70 text-sm">Progress</p>
                <div className="flex items-center gap-2">
                  <Progress value={progress} className="w-32 h-2" />
                  <span className="font-medium">{progress}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="parties">Parties</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          {/* Milestones Tab */}
          <TabsContent value="milestones">
            <Card>
              <CardHeader>
                <CardTitle>Tenancy Setup Milestones</CardTitle>
                <CardDescription>Track the progress of this tenancy</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(MILESTONE_CONFIG).map(([key, config], index) => {
                    const milestone = milestones[key] || { status: "not_started" };
                    const milestoneStatus = MILESTONE_STATUS_CONFIG[milestone.status] || MILESTONE_STATUS_CONFIG.not_started;
                    const Icon = config.icon;
                    
                    return (
                      <div 
                        key={key}
                        className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                          milestone.status === "completed" ? "bg-green-50 border-green-200" :
                          milestone.status === "in_progress" ? "bg-amber-50 border-amber-200" :
                          milestone.status === "blocked" ? "bg-red-50 border-red-200" :
                          "bg-slate-50 border-slate-200"
                        }`}
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white border">
                          <span className="text-sm font-medium text-slate-600">{index + 1}</span>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${
                              milestone.status === "completed" ? "text-green-600" :
                              milestone.status === "in_progress" ? "text-amber-600" :
                              "text-slate-400"
                            }`} />
                            <span className={`font-medium ${
                              milestone.status === "completed" ? "text-green-700" : "text-slate-700"
                            }`}>
                              {config.label}
                            </span>
                          </div>
                          {milestone.date && (
                            <p className="text-xs text-slate-500 mt-1">
                              {milestone.status === "completed" ? "Completed" : "Updated"}: {milestone.date}
                            </p>
                          )}
                        </div>
                        
                        <Select 
                          value={milestone.status} 
                          onValueChange={(v) => handleMilestoneUpdate(key, v)}
                          disabled={tenancy.status === "cancelled" || tenancy.status === "active"}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_started">Not Started</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
                
                {tenancy.status === "setup" && (
                  <div className="mt-6 pt-6 border-t">
                    <Button 
                      variant="destructive" 
                      onClick={() => setShowCancelModal(true)}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Tenancy
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parties Tab */}
          <TabsContent value="parties">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Landlord */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Landlord
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium">{tenancy.landlord_name || "Not specified"}</p>
                    {tenancy.landlord_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <Mail className="w-4 h-4" />
                        {tenancy.landlord_email}
                      </p>
                    )}
                  </div>
                  
                  {tenancy.landlord_id && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate(`/contacts/${tenancy.landlord_id}`)}
                    >
                      View Landlord Contact
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Tenant */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    Tenant
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium">{tenancy.tenant_name || "Not specified"}</p>
                    {tenancy.tenant_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <Mail className="w-4 h-4" />
                        {tenancy.tenant_email}
                      </p>
                    )}
                  </div>
                  
                  {tenancy.tenant_id && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate(`/contacts/${tenancy.tenant_id}`)}
                    >
                      View Tenant Contact
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Tenancy Details</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setShowDatesModal(true)}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Edit Dates
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Monthly Rent</p>
                      <p className="font-bold text-lg">£{(tenancy.rent_pcm || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Deposit</p>
                      <p className="font-bold text-lg">£{(tenancy.deposit || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Start Date</p>
                      <p className="font-medium">{tenancy.start_date || "Not set"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">End Date</p>
                      <p className="font-medium">{tenancy.end_date || "Not set"}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Move-in Date</p>
                    <p className="font-medium">{tenancy.move_in_date || "Not set"}</p>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Property</p>
                    <p className="font-medium">{tenancy.address}</p>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Case Created</p>
                    <p className="font-medium">{new Date(tenancy.created_at).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Linked Records</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {listing && (
                    <div 
                      className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/listings/${listing.listing_id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <Home className="w-5 h-5 text-purple-600" />
                        <span className="font-medium">Listing</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{listing.property?.full_address}</p>
                      <Badge className="mt-2" variant="outline">{listing.status}</Badge>
                    </div>
                  )}
                  
                  {offer && (
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <PoundSterling className="w-5 h-5 text-green-600" />
                        <span className="font-medium">Accepted Offer</span>
                      </div>
                      <p className="text-lg font-bold mt-1">£{(offer.offer_amount || 0).toLocaleString()} pcm</p>
                      <p className="text-sm text-slate-500">from {offer.contact_name}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dates Modal */}
      <Dialog open={showDatesModal} onOpenChange={setShowDatesModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Tenancy Dates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={datesForm.start_date}
                onChange={(e) => setDatesForm({...datesForm, start_date: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={datesForm.end_date}
                onChange={(e) => setDatesForm({...datesForm, end_date: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Move-in Date</Label>
              <Input
                type="date"
                value={datesForm.move_in_date}
                onChange={(e) => setDatesForm({...datesForm, move_in_date: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDatesModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateDates}>Update Dates</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Tenancy</DialogTitle>
            <DialogDescription>
              This will cancel the tenancy setup and re-list the property.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="mt-1"
              rows={3}
              placeholder="Why is this tenancy being cancelled?"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleCancel}>Confirm Cancellation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenancyProgressionPage;
